// Raster cache for text (spec §5.1). Each text-bearing object's text is drawn
// once per zoom bucket into an offscreen canvas and blitted afterwards, so
// panning costs one drawImage per object rather than per-glyph fillText.
//
// Buckets are raster scales in device pixels per world unit (zoom × dpr); the
// smallest bucket ≥ the current scale is used so text is only ever
// downsampled. Above the largest bucket few objects are on screen and text is
// drawn as vectors instead.
//
// Rasterising is time-budgeted per frame. When a frame runs out of budget,
// objects draw from any other bucket's raster of the same layout (or skip text
// for a frame) and `pending` asks the renderer for another frame. Zooming
// across a bucket boundary therefore refines progressively instead of
// stalling.
import type { PlacedText } from '../text/placement.ts';
import { drawText } from '../text/placement.ts';

export const BUCKETS = [0.25, 0.5, 1, 2, 4];
export const RASTER_PAD = 2; // device px around each raster
const MAX_RASTER_SIDE = 8192;

export interface Raster { image: CanvasImageSource; width: number; height: number }
export type Rasterize = (p: PlacedText, bucket: number) => Raster;

export function bucketFor(scale: number): number | null {
  for (const b of BUCKETS) if (b >= scale) return b;
  return null;
}

interface Entry {
  id: string;
  bucket: number;
  placed: PlacedText;
  raster: Raster;
  area: number;
  frame: number;
}

export interface TextCacheOptions {
  /** LRU bound on total raster pixels. */
  maxArea?: number;
  rasterize?: Rasterize;
  now?: () => number;
}

export const canvasRasterize: Rasterize = (p, bucket) => {
  const r = p.region;
  const width = Math.max(1, Math.ceil(r.w * bucket) + 2 * RASTER_PAD);
  const height = Math.max(1, Math.ceil(r.h * bucket) + 2 * RASTER_PAD);
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(bucket, 0, 0, bucket, RASTER_PAD - r.x * bucket, RASTER_PAD - r.y * bucket);
  drawText(ctx, p, bucket);
  return { image: canvas, width, height };
};

export type TextDraw =
  | { kind: 'raster'; raster: Raster; bucket: number }
  | { kind: 'vector' }
  | { kind: 'none' };

export class TextCache {
  /** Objects that wanted a raster this frame but didn't get an exact one. */
  pending = 0;
  private readonly entries = new Map<string, Entry>(); // insertion order = LRU order
  private area = 0;
  private frame = 0;
  private deadline = Infinity;
  private workDone = 0;
  private readonly maxArea: number;
  private readonly rasterize: Rasterize;
  private readonly now: () => number;

  constructor(opts: TextCacheOptions = {}) {
    this.maxArea = opts.maxArea ?? 24_000_000;
    this.rasterize = opts.rasterize ?? canvasRasterize;
    this.now = opts.now ?? (() => performance.now());
  }

  get stats(): { entries: number; area: number } {
    return { entries: this.entries.size, area: this.area };
  }

  /** Starts a frame with `budgetMs` of rasterising time. */
  beginFrame(budgetMs: number): void {
    this.frame++;
    this.pending = 0;
    this.workDone = 0;
    this.deadline = this.now() + budgetMs;
  }

  /**
   * Whether expensive text work (rasterising, layout) may run now. At least one
   * unit is always allowed per frame so progress is guaranteed.
   */
  canWork(): boolean {
    return this.workDone === 0 || this.now() < this.deadline;
  }

  /** Records a unit of text work done outside the cache (e.g. a layout). */
  noteWork(): void {
    this.workDone++;
  }

  /** Decides how to draw `placed` for object `id` at device `scale`. */
  get(id: string, placed: PlacedText, scale: number): TextDraw {
    if (placed.region.h <= 0 || placed.region.w <= 0) return { kind: 'none' };
    const bucket = bucketFor(scale);
    if (bucket === null) return { kind: 'vector' };
    if (placed.region.w * bucket > MAX_RASTER_SIDE || placed.region.h * bucket > MAX_RASTER_SIDE) return { kind: 'vector' };

    const key = `${id}|${bucket}`;
    let e = this.entries.get(key);
    if (e && e.placed !== placed) {
      this.remove(key, e);
      e = undefined;
    }
    if (e) {
      this.touch(key, e);
      return { kind: 'raster', raster: e.raster, bucket };
    }
    if (this.canWork()) {
      this.workDone++;
      const raster = this.rasterize(placed, bucket);
      e = { id, bucket, placed, raster, area: raster.width * raster.height, frame: this.frame };
      this.entries.set(key, e);
      this.area += e.area;
      this.evict();
      return { kind: 'raster', raster, bucket };
    }

    this.pending++;
    for (const b of BUCKETS) {
      const otherKey = `${id}|${b}`;
      const other = this.entries.get(otherKey);
      if (other && other.placed === placed) {
        this.touch(otherKey, other);
        return { kind: 'raster', raster: other.raster, bucket: b };
      }
    }
    return { kind: 'none' };
  }

  forget(id: string): void {
    for (const b of BUCKETS) {
      const key = `${id}|${b}`;
      const e = this.entries.get(key);
      if (e) this.remove(key, e);
    }
  }

  clear(): void {
    this.entries.clear();
    this.area = 0;
  }

  private touch(key: string, e: Entry): void {
    e.frame = this.frame;
    this.entries.delete(key);
    this.entries.set(key, e);
  }

  private remove(key: string, e: Entry): void {
    this.entries.delete(key);
    this.area -= e.area;
  }

  /**
   * Drops least-recently-used rasters, never ones used in the current frame.
   * Every use moves an entry to the end, so current-frame entries form the tail.
   */
  private evict(): void {
    for (const [key, e] of this.entries) {
      if (this.area <= this.maxArea || e.frame === this.frame) break;
      this.remove(key, e);
    }
  }
}
